/**
 * Vendor Configuration
 * Configure which vendors are used on the site and their associated cookies
 */

/**
 * Configured vendors for this site
 * Each vendor includes their GVL ID and associated cookies to clear on reject
 */
export const configuredVendors = [
  {
    id: 755,
    name: 'Google Advertising Products',
    description: 'Google Analytics, Google Ads, YouTube',
    cookies: [
      '_ga',           // Main GA cookie (2 years)
      '_gid',          // 24-hour tracking cookie
      '_gat',          // Throttle request rate (1 min)
      '_gat_gtag_*',   // Google Analytics gtag
      '_ga_*',         // GA4 measurement ID cookies
      '__utma',        // Legacy Universal Analytics
      '__utmb',        // Legacy UA session
      '__utmc',        // Legacy UA session
      '__utmt',        // Legacy UA throttle
      '__utmz',        // Legacy UA campaign
      '_gcl_au',       // Google Ads conversion linker
      '_gcl_aw',       // Google Ads click ID
      '_gac_*',        // Google Ads conversion
      'IDE',           // DoubleClick
      'DSID',          // DoubleClick
      '__gads',        // Google Ad Manager
      '__gpi',         // Google Publisher tag
    ]
  }
];

/**
 * Get all configured vendor IDs
 */
export function getConfiguredVendorIds() {
  return configuredVendors.map(v => v.id);
}

/**
 * Get vendor configuration by ID
 */
export function getVendorConfig(vendorId) {
  return configuredVendors.find(v => v.id === vendorId);
}

/**
 * Delete a cookie by name on all possible domains and paths
 * Google Analytics sets cookies on the root domain with leading dot
 */
function deleteCookieCompletely(cookieName) {
  const hostname = window.location.hostname;
  const paths = ['/', window.location.pathname, ''];
  
  // Build comprehensive list of domains to try
  // GA typically sets on ".cookiebikes.com" (with leading dot)
  const domains = [
    '',                    // No domain (current document domain)
    hostname,              // Exact hostname: www.cookiebikes.com
    '.' + hostname,        // With leading dot: .www.cookiebikes.com
  ];
  
  // Add all parent domains: .cookiebikes.com, .com
  const parts = hostname.split('.');
  for (let i = 1; i < parts.length; i++) {
    const parentDomain = parts.slice(i).join('.');
    domains.push(parentDomain);
    domains.push('.' + parentDomain);
  }
  
  // Try all combinations of domain, path, and SameSite
  const sameSiteOptions = ['', 'SameSite=Lax', 'SameSite=Strict', 'SameSite=None;Secure'];
  
  domains.forEach(d => {
    paths.forEach(p => {
      sameSiteOptions.forEach(ss => {
        const domainAttr = d ? `domain=${d};` : '';
        const pathAttr = `path=${p || '/'};`;
        const sameSiteAttr = ss ? `${ss};` : '';
        const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT;';
        
        document.cookie = `${cookieName}=;${expiry}${domainAttr}${pathAttr}${sameSiteAttr}`;
      });
    });
  });
}

/**
 * Check if a cookie name matches a pattern (supports * wildcard at end)
 */
function matchesCookiePattern(cookieName, pattern) {
  if (pattern.endsWith('*')) {
    return cookieName.startsWith(pattern.slice(0, -1));
  }
  return cookieName === pattern;
}

/**
 * Delete all tracking cookies for a specific vendor
 */
export function deleteVendorCookies(vendorId) {
  const vendor = getVendorConfig(vendorId);
  if (!vendor || !vendor.cookies) return;
  
  const allCookies = document.cookie.split(';');
  let deletedCount = 0;
  
  allCookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    
    const shouldDelete = vendor.cookies.some(pattern => 
      matchesCookiePattern(cookieName, pattern)
    );
    
    if (shouldDelete) {
      deleteCookieCompletely(cookieName);
      deletedCount++;
      console.log(`🍪 Deleted cookie: ${cookieName} (${vendor.name})`);
    }
  });
  
  if (deletedCount > 0) {
    console.log(`✓ Cleared ${deletedCount} cookies for ${vendor.name}`);
  }
}

/**
 * Delete all tracking cookies from all configured vendors
 * Called when user clicks "Reject"
 */
export function deleteAllTrackingCookies() {
  console.log('🗑️ Clearing tracking cookies...');
  
  configuredVendors.forEach(vendor => {
    deleteVendorCookies(vendor.id);
  });
  
  console.log('✓ All tracking cookies cleared');
}

/**
 * Get a summary of cookies that will be used/cleared
 */
export function getCookieSummary() {
  return configuredVendors.map(vendor => ({
    vendor: vendor.name,
    cookiePatterns: vendor.cookies.length,
    description: vendor.description
  }));
}

/**
 * Disable Google Analytics tracking
 * Sets the GA opt-out flag and deletes existing cookies
 */
export function disableGoogleAnalytics() {
  // Set the GA opt-out window property
  // This prevents GA from setting new cookies
  window['ga-disable-' + (window.GA_MEASUREMENT_ID || '')] = true;
  
  // Also set gtag consent mode to denied
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });
  }
  
  // Delete existing GA cookies
  deleteVendorCookies(755);
  
  console.log('🚫 Google Analytics disabled');
}

/**
 * Enable Google Analytics tracking (after consent)
 */
export function enableGoogleAnalytics() {
  // Remove the opt-out flag
  window['ga-disable-' + (window.GA_MEASUREMENT_ID || '')] = false;
  
  // Update gtag consent mode to granted
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': 'granted',
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted'
    });
  }
  
  console.log('✅ Google Analytics enabled');
}

/**
 * Initialize consent mode for Google tags
 * Call this BEFORE loading gtag.js
 */
export function initGoogleConsentMode(hasConsent = false) {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  
  // Set default consent state
  gtag('consent', 'default', {
    'analytics_storage': hasConsent ? 'granted' : 'denied',
    'ad_storage': hasConsent ? 'granted' : 'denied',
    'ad_user_data': hasConsent ? 'granted' : 'denied',
    'ad_personalization': hasConsent ? 'granted' : 'denied',
    'wait_for_update': 500
  });
  
  console.log(`🔒 Google Consent Mode initialized: ${hasConsent ? 'granted' : 'denied'}`);
}
